// app/frontend/pages/Posts/PostIndex.tsx

import { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import {
  Box,
  Container,
  Grid,
  Title,
  Text,
  TextInput,
  Group,
  MultiSelect,
  ActionIcon,
  Drawer,
  Button,
  Stack,
  Divider,
  Loader,
  MantineProvider,
  Badge,
} from '@mantine/core';
import { Head, router } from '@inertiajs/react';
import { IconSearch, IconAdjustmentsHorizontal } from '@tabler/icons-react';
import ContentPostCard from '../../components/Cards/ContentPostCard';
import { StablePagination } from '../../components/StablePagination';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import PostDrawerContent from '../../components/Drawer/PostDrawerContent';

interface PostType {
  id: number;
  name: string;
}
interface Author {
  id: number;
  name: string;
}
export interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  published_at: string;
  author: Author;
  categories: { id: number; name: string }[];
  tags: { id: number; name: string }[];
  post_types: PostType[];
  [key: string]: any;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}
interface FiltersData {
  search?: string;
  post_types?: string;
}

interface PostIndexProps {
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
  posts: Post[];
  totalPosts: number;
  postTypes: PostType[];
  pagination: PaginationData;
  filters: FiltersData;
}

export default function PostIndex({ posts, totalPosts, postTypes, pagination, filters, page_meta }: PostIndexProps) {
  // --- existing state/hooks ---
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [selectedPostTypes, setSelectedPostTypes] = useState<string[]>(
    filters.post_types ? filters.post_types.split(',') : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);
  const [currentPage, setCurrentPage] = useState(pagination.current_page);

  // --- NEW: dynamic-drawer state ---
  const [postDrawerOpened, setPostDrawerOpened] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  const activeFilterCount = selectedPostTypes.length;
  const postTypeOptions = postTypes.map((pt) => ({ value: pt.id.toString(), label: pt.name }));

  const loadPosts = (page: number = 1) => {
    setIsLoading(true);
    const params: Record<string, any> = { page, items: 20 };
    if (searchTerm) params.search = searchTerm;
    if (selectedPostTypes.length) params.post_types = selectedPostTypes.join(',');
    router.visit('/posts', {
      data: params,
      preserveState: true,
      onSuccess: () => setIsLoading(false),
      onError: () => setIsLoading(false),
    });
  };

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    // console.log('🤓 Post Index Page meta:', page_meta);
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== filters.search) loadPosts(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(pagination.current_page);
  }, [pagination.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadPosts(page);
  };

  const handleApplyFilters = () => {
    loadPosts(1);
    setFilterDrawerOpened(false);
  };

  const handleClearFilters = () => {
    setSelectedPostTypes([]);
  };

  // --- NEW: open/close drawer ---
  const handleCardClick = (post: Post) => {
    setSelectedPost(post);
    setPostDrawerOpened(true);
  };

  const handleDrawerClose = () => {
    setPostDrawerOpened(false);
    setSelectedPost(null);
  };

  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <Head title="Posts" />

      <Container size="md" mt="md" mb="8rem" style={{ minHeight: '100vh' }}>
        {/* Search + Filter Bar */}
        <Box mb="xl">
          <Group align="center">
            <TextInput
              leftSection={<IconSearch size={14} />}
              placeholder="Search posts by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              w={{ base: '80%', md: '600px' }}
            />
            <Box pos="relative">
              <ActionIcon variant="light" size="xl" color="orange" onClick={() => setFilterDrawerOpened(true)}>
                <IconAdjustmentsHorizontal />
              </ActionIcon>
              {activeFilterCount > 0 && (
                <Badge
                  color="orange"
                  size="sm"
                  radius="xl"
                  style={{ position: 'absolute', top: -8, right: -8, padding: '4px 8px' }}
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Box>
          </Group>
        </Box>

        {/* Results Header */}
        <Box mb="md">
          <Text c="#fff" className="poppins">
            Showing {posts.length} of {totalPosts} posts.
            {isLoading && <Loader color="orange" size="xs" ml="md" />}
          </Text>
        </Box>

        {/* Top Pagination */}
        {pagination.total_pages > 1 && (
          <Group mb="md" align="left">
            <StablePagination
              total={pagination.total_pages}
              value={currentPage}
              onChange={handlePageChange}
              color="orange"
            />
          </Group>
        )}

        {/* Post Grid */}
        <Grid gutter="md">
          {posts.map((post) => (
            <Grid.Col span={{ base: 12, sm: 4, md: 4, lg: 4 }} key={post.id}>
              <Box style={{ cursor: 'pointer' }}>
                <ContentPostCard post={post} onCardClick={handleCardClick} />
              </Box>
            </Grid.Col>
          ))}
        </Grid>

        {posts.length === 0 && (
          <Text align="center" size="lg" weight={500} mt="xl">
            No posts match your search and filter criteria.
          </Text>
        )}

        {/* Bottom Pagination */}
        {pagination.total_pages > 1 && (
          <Group mt="md" align="left">
            <StablePagination
              total={pagination.total_pages}
              value={currentPage}
              onChange={handlePageChange}
              color="orange"
            />
          </Group>
        )}
      </Container>

      {/* Filter Drawer */}
      <Drawer
        opened={filterDrawerOpened}
        onClose={() => setFilterDrawerOpened(false)}
        position="right"
        size="md"
        withCloseButton
        styles={{
          root: { zIndex: 1000 },
          header: { backgroundColor: '#18181B', color: '#f2f2f2', borderBottom: '1px solid #333' },
          body: { backgroundColor: '#18181B', color: '#f2f2f2', minHeight: '100vh' },
          close: { color: '#f2f2f2', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } },
        }}
      >
        <Stack gap="md">
          <Title mt="2rem" order={4}>
            Filters
          </Title>
          <Divider mb="3rem" />

          <Box>
            <Text fw={500} mb="3rem">
              Post Types
            </Text>
            <MultiSelect
              data={postTypeOptions}
              placeholder={selectedPostTypes.length === 0 ? 'Select post types' : ''}
              value={selectedPostTypes}
              onChange={setSelectedPostTypes}
              clearable
              searchable
              my="md"
              size="md"
              w="100%"
            />
          </Box>

          <Divider />

          <Group justify="apart">
            <Button variant="light" color="#EA580C" onClick={handleClearFilters} disabled={activeFilterCount === 0}>
              Clear All
            </Button>
            <Button color="#EA580C" onClick={handleApplyFilters}>
              Show Posts
            </Button>
          </Group>
        </Stack>
      </Drawer>

      {/* Post Dynamic Drawer */}
      {selectedPost && (
        <DynamicDrawer opened={postDrawerOpened} type="post" data={selectedPost} onClose={handleDrawerClose}>
          <PostDrawerContent post={selectedPost} />
        </DynamicDrawer>
      )}
    </MantineProvider>
  );
}
